import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Plus, Trash, FilePlus, CaretDown, CaretRight, Lock, CheckCircle, Warning,
  X, FloppyDisk, ArrowsClockwise,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { libelleStatutAvenant } from './AvenantFiche';

// RP67-68 — un avenant soumis au client (Proposé), accepté ou validé (ancien circuit)
// n'est plus modifiable ; refusé, il peut être corrigé puis soumis à nouveau.
const STATUTS_FIGES = ['VALIDE', 'PROPOSE', 'ACCEPTE'];

const API_URL = process.env.REACT_APP_BACKEND_URL;

function genId(prefix) { return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`; }

function formatDateTimeFR(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
}

// ============================================================
// Editor d'un seul avenant (modal)
// ============================================================
export function AvenantEditor({ open, onClose, chantierId, avenantId, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveTimeout = useRef(null);

  const load = useCallback(() => {
    if (!avenantId) return;
    axios.get(`${API_URL}/api/chantiers/${chantierId}/dpgf/avenants/${avenantId}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Erreur chargement avenant'));
  }, [chantierId, avenantId]);

  useEffect(() => {
    if (!open) { setData(null); return; }
    load();
  }, [open, load]);

  const isLocked = STATUTS_FIGES.includes(data?.statut);
  const [soumettreOpen, setSoumettreOpen] = useState(false);

  const autoSave = useCallback((next) => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      try {
        setSaving(true);
        const r = await axios.put(
          `${API_URL}/api/chantiers/${chantierId}/dpgf/avenants/${avenantId}`,
          { label: next.label, motif: next.motif, lots: next.lots },
          { withCredentials: true }
        );
        setData(r.data);
        onSaved?.();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erreur enregistrement');
      } finally {
        setSaving(false);
      }
    }, 700);
  }, [chantierId, avenantId, onSaved]);

  const update = (patch) => {
    if (isLocked) return;
    const next = { ...data, ...patch };
    setData(next);
    autoSave(next);
  };

  const updateLots = (newLots) => update({ lots: newLots });

  const addLot = () => {
    updateLots([...(data.lots || []), { id: genId('avlot'), nom: 'Lot avenant', ref_lot_id: null, postes: [] }]);
  };
  const removeLot = (idx) => {
    if (!window.confirm('Supprimer ce lot d\'avenant ?')) return;
    updateLots(data.lots.filter((_, i) => i !== idx));
  };
  const updateLot = (idx, patch) => {
    const newLots = [...data.lots];
    newLots[idx] = { ...newLots[idx], ...patch };
    updateLots(newLots);
  };

  const addPoste = (lotIdx) => {
    const lots = [...data.lots];
    const newPoste = {
      id: genId('avp'),
      ref_poste_id: null,
      designation: '',
      unite: '',
      quantite: 0,
      prix_unitaire: 0,
      motif: '',
    };
    lots[lotIdx] = { ...lots[lotIdx], postes: [...(lots[lotIdx].postes || []), newPoste] };
    updateLots(lots);
  };
  const removePoste = (lotIdx, pIdx) => {
    const lots = [...data.lots];
    lots[lotIdx] = { ...lots[lotIdx], postes: lots[lotIdx].postes.filter((_, i) => i !== pIdx) };
    updateLots(lots);
  };
  const updatePoste = (lotIdx, pIdx, patch) => {
    const lots = [...data.lots];
    const postes = [...lots[lotIdx].postes];
    postes[pIdx] = { ...postes[pIdx], ...patch };
    lots[lotIdx] = { ...lots[lotIdx], postes };
    updateLots(lots);
  };

  // RP67 (D30, MOA-A4) — l'architecte ne valide plus seul : il soumet l'avenant au client,
  // qui l'accepte ou le refuse avec un code de vérification.
  const onSoumis = () => { setSoumettreOpen(false); load(); onSaved?.(); };

  if (!open) return null;
  if (!data) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
        <DialogContent className="max-w-4xl p-4"><Skeleton className="h-32" /></DialogContent>
      </Dialog>
    );
  }

  const totalHt = (data.lots || []).reduce(
    (s, l) => s + (l.postes || []).reduce((s2, p) => s2 + ((p.quantite || 0) * (p.prix_unitaire || 0)), 0), 0
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-5xl p-0 max-h-[92vh] flex flex-col" data-testid="avenant-editor-modal">
        <DialogHeader className="px-4 py-3 border-b border-[#E0E0DB] bg-[#FAFAF8]">
          <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'DM Serif Display, serif' }}>
            <FilePlus size={20} className="text-[#1E3A5F]" weight="duotone" />
            Avenant n°{data.numero}
            {data.statut && data.statut !== 'BROUILLON' && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase bg-[#2D6A4F] text-white" data-testid="avenant-statut-badge">
                {isLocked && <Lock size={10} weight="fill" />} {libelleStatutAvenant(data)}
              </span>
            )}
            {saving && <span className="ml-auto text-[10px] text-[#9A9A9A] flex items-center gap-1"><ArrowsClockwise size={10} className="animate-spin" /> Sauvegarde…</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 border-b border-[#E0E0DB] bg-white space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-[#9A9A9A] font-bold block mb-1">Label</label>
              <Input
                value={data.label || ''}
                onChange={(e) => update({ label: e.target.value })}
                disabled={isLocked}
                placeholder="Avenant n°..."
                className="rounded-none h-8 text-sm"
                data-testid="avenant-label-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-[#9A9A9A] font-bold block mb-1">Motif</label>
              <Input
                value={data.motif || ''}
                onChange={(e) => update({ motif: e.target.value })}
                disabled={isLocked}
                placeholder="Modification client / découverte chantier / …"
                className="rounded-none h-8 text-sm"
                data-testid="avenant-motif-input"
              />
            </div>
          </div>
          <div className="text-xs text-[#6B6B6B]">
            Créé le {formatDateTimeFR(data.cree_le)}
            {data.valide_le && <> · Validé le {formatDateTimeFR(data.valide_le)}</>}
            {data.propose_le && <> · Soumis au client le {formatDateTimeFR(data.propose_le)}</>}
          </div>
          {data.statut === 'REFUSE' && data.decision && (
            <div className="text-xs border-l-4 border-[#C53030] bg-[#FEF2F2] px-3 py-2" data-testid="avenant-refus">
              Refusé par le client le {formatDateTimeFR(data.decision.le)}
              {data.decision.commentaire && <> : « {data.decision.commentaire} »</>}. Corrigez l'avenant puis soumettez-le à nouveau.
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-4 bg-[#FAFAF8]">
          {(data.lots || []).map((lot, lotIdx) => {
            const lotTotal = (lot.postes || []).reduce((s, p) => s + ((p.quantite || 0) * (p.prix_unitaire || 0)), 0);
            return (
              <div key={lot.id} className="mb-4 bg-white border border-[#E0E0DB]" data-testid={`avenant-lot-${lot.id}`}>
                <div className="bg-[#1E3A5F] text-white px-3 py-2 flex items-center gap-2">
                  <Input
                    value={lot.nom}
                    onChange={(e) => updateLot(lotIdx, { nom: e.target.value })}
                    disabled={isLocked}
                    className="bg-transparent border-0 text-white font-bold text-sm rounded-none h-7 outline-none"
                    placeholder="Nom du lot"
                  />
                  <span className="text-sm font-bold whitespace-nowrap">{lotTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  {!isLocked && (
                    <button onClick={() => removeLot(lotIdx)} className="text-white/70 hover:text-white" title="Supprimer ce lot d'avenant">
                      <Trash size={14} />
                    </button>
                  )}
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAF8] border-b border-[#E0E0DB]">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-[#2C2C2C]" style={{ width: '38%' }}>Désignation</th>
                      <th className="px-2 py-1.5 text-left font-medium text-[#2C2C2C]">Unité</th>
                      <th className="px-2 py-1.5 text-right font-medium text-[#2C2C2C]">Qté</th>
                      <th className="px-2 py-1.5 text-right font-medium text-[#2C2C2C]">PU HT €</th>
                      <th className="px-2 py-1.5 text-right font-medium text-[#2C2C2C]">Total €</th>
                      <th className="px-2 py-1.5 text-left font-medium text-[#2C2C2C]" style={{ width: '22%' }}>Motif</th>
                      <th className="px-2 py-1.5 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(lot.postes || []).length === 0 && (
                      <tr><td colSpan={7} className="px-2 py-3 text-center text-xs text-[#9A9A9A] italic">Aucun poste — cliquez sur « + Ajouter un poste »</td></tr>
                    )}
                    {(lot.postes || []).map((p, pIdx) => {
                      const total = (p.quantite || 0) * (p.prix_unitaire || 0);
                      return (
                        <tr key={p.id} className="border-b border-[#F0F0EE]" data-testid={`avenant-poste-${p.id}`}>
                          <td className="px-1 py-0.5">
                            <input value={p.designation} onChange={(e) => updatePoste(lotIdx, pIdx, { designation: e.target.value })} disabled={isLocked}
                              placeholder="Désignation" className="w-full px-2 py-1 border border-transparent hover:border-[#E0E0DB] focus:border-[#C75B39] outline-none bg-transparent" />
                          </td>
                          <td className="px-1 py-0.5">
                            <input value={p.unite || ''} onChange={(e) => updatePoste(lotIdx, pIdx, { unite: e.target.value })} disabled={isLocked}
                              placeholder="m²" className="w-full px-2 py-1 border border-transparent hover:border-[#E0E0DB] focus:border-[#C75B39] outline-none bg-transparent" />
                          </td>
                          <td className="px-1 py-0.5">
                            <input type="number" step="0.01" value={p.quantite || 0} onChange={(e) => updatePoste(lotIdx, pIdx, { quantite: parseFloat(e.target.value) || 0 })} disabled={isLocked}
                              className="w-full px-2 py-1 text-right border border-transparent hover:border-[#E0E0DB] focus:border-[#C75B39] outline-none bg-transparent" />
                          </td>
                          <td className="px-1 py-0.5">
                            <input type="number" step="0.01" value={p.prix_unitaire || 0} onChange={(e) => updatePoste(lotIdx, pIdx, { prix_unitaire: parseFloat(e.target.value) || 0 })} disabled={isLocked}
                              className="w-full px-2 py-1 text-right border border-transparent hover:border-[#E0E0DB] focus:border-[#C75B39] outline-none bg-transparent" />
                          </td>
                          <td className="px-2 py-1 text-right font-medium">{total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                          <td className="px-1 py-0.5">
                            <input value={p.motif || ''} onChange={(e) => updatePoste(lotIdx, pIdx, { motif: e.target.value })} disabled={isLocked}
                              placeholder="Justification" className="w-full px-2 py-1 border border-transparent hover:border-[#E0E0DB] focus:border-[#C75B39] outline-none bg-transparent text-[10px] italic text-[#6B6B6B]" />
                          </td>
                          <td className="px-1 py-0.5 text-center">
                            {!isLocked && (
                              <button onClick={() => removePoste(lotIdx, pIdx)} className="text-[#9A9A9A] hover:text-[#8B3A3A]" data-testid={`avenant-poste-delete-${p.id}`}>
                                <Trash size={12} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!isLocked && (
                  <div className="px-2 py-1 border-t border-[#E0E0DB]">
                    <button onClick={() => addPoste(lotIdx)} className="text-xs text-[#1E3A5F] hover:text-[#15293F] flex items-center gap-1 py-1" data-testid={`avenant-add-poste-${lot.id}`}>
                      <Plus size={12} weight="bold" /> Ajouter un poste
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {!isLocked && (
            <Button variant="outline" className="rounded-none border-dashed gap-1 mt-2" onClick={addLot} data-testid="avenant-add-lot">
              <Plus size={14} weight="bold" /> Ajouter un lot d'avenant
            </Button>
          )}
        </div>

        <div className="border-t border-[#E0E0DB] bg-white px-4 py-3 flex items-center justify-between">
          <div className="text-sm">
            <span className="text-[10px] text-[#9A9A9A] uppercase tracking-wide block">Montant avenant HT</span>
            <span className="text-lg font-bold text-[#1E3A5F]" style={{ fontFamily: 'DM Serif Display, serif' }} data-testid="avenant-total-ht">
              {totalHt.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={onClose}>Fermer</Button>
            {!isLocked && (
              <Button size="sm" className="rounded-none bg-[#2D6A4F] hover:bg-[#225538] text-white gap-1" onClick={() => setSoumettreOpen(true)} data-testid="avenant-soumettre-btn">
                <CheckCircle size={14} weight="fill" /> Soumettre au client
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
      {soumettreOpen && (
        <SoumettreAuClientDialog chantierId={chantierId} avenant={data} onClose={() => setSoumettreOpen(false)} onDone={onSoumis} />
      )}
    </Dialog>
  );
}

// ============================================================
// RP67 — Soumettre au client : fiche lisible (pourquoi, effet sur le délai, marché)
// ============================================================
function SoumettreAuClientDialog({ chantierId, avenant, onClose, onDone }) {
  const [motif, setMotif] = useState(avenant.motif || '');
  const [delai, setDelai] = useState(avenant.effet_delai_jours ?? 0);
  const [lotMarche, setLotMarche] = useState('');
  const [marches, setMarches] = useState([]);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true })
      .then((r) => setMarches(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMarches([]));
  }, [chantierId]);

  const envoyer = async () => {
    if (!motif.trim()) { toast.error('Indiquez pourquoi cet avenant est nécessaire : le client le lira.'); return; }
    setEnvoi(true);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/avenants/${avenant.id}/proposer`,
        { motif, effet_delai_jours: parseInt(delai, 10) || 0, lot_marche_id: lotMarche || null },
        { withCredentials: true },
      );
      toast.success("Avenant soumis à l'accord du client");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la soumission');
    } finally { setEnvoi(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="avenant-soumettre-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Soumettre l'avenant n°{avenant.numero} au client</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6B6B6B]">
          Le client reçoit une fiche lisible (quoi, pourquoi, montant en plus ou en moins, nouveau total du marché,
          effet sur le délai) et l'accepte ou la refuse avec un code de vérification. L'avenant n'est plus modifiable
          tant qu'il attend sa décision.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#9A9A9A] font-bold block mb-1">Pourquoi (lu par le client)</label>
            <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3}
              className="w-full border border-[#E0E0DB] px-2 py-1 text-sm" data-testid="avenant-soumettre-motif" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#9A9A9A] font-bold block mb-1">Effet sur le délai (jours, négatif s'il le réduit)</label>
            <Input type="number" value={delai} onChange={(e) => setDelai(e.target.value)} className="rounded-none w-32"
              data-testid="avenant-soumettre-delai" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#9A9A9A] font-bold block mb-1">Marché concerné (si le lot de l'avenant n'est pas reconnu)</label>
            <select value={lotMarche} onChange={(e) => setLotMarche(e.target.value)}
              className="w-full border border-[#E0E0DB] px-2 py-1 text-sm bg-white" data-testid="avenant-soumettre-lot">
              <option value="">— Retrouvé automatiquement —</option>
              {marches.map((m) => <option key={m.id} value={m.id}>{m.code} {m.nom}{m.entreprise_nom ? ` — ${m.entreprise_nom}` : ''}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
          <Button className="rounded-none bg-[#2D6A4F] hover:bg-[#225538] text-white" disabled={envoi} onClick={envoyer}
            data-testid="avenant-soumettre-envoyer">
            Soumettre au client
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Liste des avenants + bouton créer
// ============================================================
export default function DPGFAvenantsManager({ chantierId, dpgfStatus }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/chantiers/${chantierId}/dpgf/avenants`, { withCredentials: true })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [chantierId]);

  useEffect(() => { load(); }, [load]);

  const isLocked = dpgfStatus === 'VALIDEE';

  const createNew = async () => {
    const label = window.prompt('Libellé de l\'avenant ? (ex: "Avenant n°1 — Modif client")', `Avenant n°${items.length + 1}`);
    if (label === null) return;
    try {
      const r = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/avenants`,
        { label: label || undefined },
        { withCredentials: true }
      );
      toast.success('Avenant créé');
      load();
      setEditingId(r.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création avenant');
    }
  };

  const removeAvenant = async (avid) => {
    if (!window.confirm('Supprimer cet avenant ? (irréversible)')) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/dpgf/avenants/${avid}`, { withCredentials: true });
      toast.success('Avenant supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  if (!isLocked && items.length === 0) return null; // pas d'UI si DPGF non verrouillée et aucun avenant

  const totalAvenants = items.reduce((s, a) => s + (a.montant_ht || 0), 0);

  return (
    <div className="mt-6 border border-[#E0E0DB] bg-white" data-testid="dpgf-avenants-section">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full bg-[#1E3A5F] text-white px-4 py-2 flex items-center justify-between hover:bg-[#15293F]"
        data-testid="dpgf-avenants-toggle"
      >
        <div className="flex items-center gap-2">
          {collapsed ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
          <FilePlus size={16} weight="duotone" />
          <span className="text-sm font-bold">Avenants ({items.length})</span>
          {totalAvenants !== 0 && (
            <span className="text-xs bg-white/15 px-2 py-0.5">+{totalAvenants.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
          )}
        </div>
        {!isLocked && (
          <span className="inline-flex items-center gap-1 text-[10px] text-white/70">
            <Warning size={10} weight="fill" /> DPGF non verrouillée
          </span>
        )}
      </button>

      {!collapsed && (
        <div className="p-3">
          {!isLocked && items.length === 0 && (
            <p className="text-xs text-[#9A9A9A] italic">Verrouillez la DPGF pour DCE pour pouvoir créer des avenants.</p>
          )}
          {loading && <Skeleton className="h-12" />}
          {!loading && items.length > 0 && (
            <ul className="divide-y divide-[#E0E0DB] border border-[#E0E0DB] mb-3">
              {items.map((av) => {
                const validated = STATUTS_FIGES.includes(av.statut);
                return (
                  <li key={av.id} className="px-3 py-2 flex items-center gap-3 hover:bg-[#FAFAF8]" data-testid={`dpgf-avenant-row-${av.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-[#2C2C2C]">N°{av.numero}</span>
                        <span className="text-sm text-[#2C2C2C] truncate">{av.label}</span>
                        {validated ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-[#2D6A4F] text-white px-1.5 py-0.5">
                            <Lock size={9} weight="fill" /> {libelleStatutAvenant(av)}
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase bg-[#FFF3E0] text-[#C4773B] px-1.5 py-0.5 border border-[#C4773B66]">
                            {libelleStatutAvenant(av)}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#9A9A9A]">
                        {formatDateTimeFR(av.cree_le)}
                        {av.motif && ` · ${av.motif}`}
                      </div>
                    </div>
                    <div className="text-sm font-medium text-[#1E3A5F] whitespace-nowrap">
                      {(av.montant_ht || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="h-7 rounded-none text-xs gap-1" onClick={() => setEditingId(av.id)} data-testid={`dpgf-avenant-open-${av.id}`}>
                        {validated ? 'Voir' : 'Éditer'}
                      </Button>
                      {!validated && (
                        <button onClick={() => removeAvenant(av.id)} className="text-[#9A9A9A] hover:text-[#8B3A3A] px-1" data-testid={`dpgf-avenant-delete-${av.id}`}>
                          <Trash size={12} />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {isLocked && (
            <Button variant="outline" className="rounded-none border-dashed gap-1 text-[#1E3A5F] border-[#1E3A5F]" onClick={createNew} data-testid="dpgf-avenant-create">
              <Plus size={14} weight="bold" /> Créer un avenant
            </Button>
          )}
        </div>
      )}

      {editingId && (
        <AvenantEditor
          open
          onClose={() => setEditingId(null)}
          chantierId={chantierId}
          avenantId={editingId}
          onSaved={load}
        />
      )}
    </div>
  );
}
